import React from 'react'
import { camelCase } from 'lodash'
import BlockedAnnouncement from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockedAnnouncement.react'
import BlockReason from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockReason.enum'
import {
  isForeignMunicipality,
  NO_MUNICIPALITY_IN_FINLAND,
  UNKNOWN_MUNICIPALITY,
} from 'common/src/vtj/municipality.util'
import { TodistuksetUser } from 'todistukset-common/src/vtj/types/user'
import {
  isEidasIdentifiedUser,
  isHetuIdentifiedUser,
} from 'todistukset-common/src/vtj/user.utils'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { CertificateFeature } from 'todistukset-common/src/vtj/CertificateFeature.enum'
import { getEnabledFeatures } from 'todistukset-common/src/vtj/certificate.util'
import { maritalStatusBlocksAet } from 'todistukset-common/src/vtj/MaritalStatus.util'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import { MaritalStatus } from 'todistukset-common/src/vtj/MaritalStatus.enum'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'

const DISALLOWED_MUNICIPALITIES = [
  UNKNOWN_MUNICIPALITY,
  NO_MUNICIPALITY_IN_FINLAND,
]

const isDisallowedMunicipality = (user: TodistuksetUser): boolean => {
  if (
    isEidasIdentifiedUser(user) &&
    user.kotikuntaKoodi === UNKNOWN_MUNICIPALITY
  ) {
    // We don't know the user's municipality
    return false
  }
  return Boolean(
    user.kotikuntaKoodi &&
      DISALLOWED_MUNICIPALITIES.includes(user.kotikuntaKoodi)
  )
}

const isInRegisteredParnership = (user: TodistuksetUser): boolean =>
  isHetuIdentifiedUser(user) &&
  finalEditableFieldValue(user.maritalStatus) ===
    MaritalStatus.REKISTEROIDYSSA_PARISUHTEESSA

const isMarried = (user: TodistuksetUser): boolean => {
  if (isHetuIdentifiedUser(user)) {
    const maritalStatusValue = finalEditableFieldValue(user.maritalStatus)
    if (maritalStatusValue) {
      return maritalStatusBlocksAet(maritalStatusValue)
    }
  }
  return false
}

export const blockReasonTitleKey = (blockReason: BlockReason): string =>
  `${camelCase(blockReason)}Title`

export const blockReasonContentKey = (blockReason: BlockReason): string =>
  `${camelCase(blockReason)}Content`

/**
 * Return BlockReasons describing why the user can't use this service to order any certificates.
 */
export const globalBlockReasons = (
  user: TodistuksetUser | undefined
): BlockReason[] => {
  if (!user) return []
  const globalBlockedChecks: [boolean, BlockReason][] = [
    [user.isSafetyProhibitedMinor, BlockReason.SAFETY_PROHIBITED_MINOR],
    [!user.isOver15, BlockReason.UNDER_15],
  ]
  return globalBlockedChecks
    .filter(([isBlocked]) => isBlocked)
    .map(([, reason]) => reason)
}

/**
 * Returns a BlockReason describing why the user can't order this particular certificate.
 */
export const userBlockedReason = (
  user: TodistuksetUser | undefined,
  certificateType: CertificateTypes,
  orderFlowName: OrderFlowName | undefined = undefined
): BlockReason | undefined => {
  if (!user) {
    return undefined
  }

  const isAetSignerFlow = orderFlowName === OrderFlowName.AETTODISTUS_SIGNER

  function hasFeature(feature: CertificateFeature): boolean {
    return (
      (user &&
        certificateType &&
        getEnabledFeatures({ certificateType, isAdult: user.isAdult }).includes(
          feature
        )) ??
      false
    )
  }

  const certificateFeatureBlockedChecks: [boolean, BlockReason][] = [
    [
      !hasFeature(CertificateFeature.UNKNOWN_OR_NO_MUNICIPALITY_ALLOWED) &&
        isDisallowedMunicipality(user),
      BlockReason.INVALID_MUNICIPALITY,
    ],
    [
      !hasFeature(CertificateFeature.ALAND_ALLOWED) && user.isAlandResident,
      BlockReason.ALAND_RESIDENT,
    ],
    // We do not block a married signer because he/she needs to be able to reject the order. For a signer
    // the validation will be done later in the order wizard.
    [
      hasFeature(CertificateFeature.REGISTERED_PARTNERSHIP_DISALLOWED) &&
        !isAetSignerFlow &&
        isInRegisteredParnership(user),
      BlockReason.IN_REGISTERED_PARTNERSHIP,
    ],
    // this check will also block for user in registered partnership, but we only show the first error
    [
      hasFeature(CertificateFeature.REGISTERED_PARTNERSHIP_DISALLOWED) &&
        !isAetSignerFlow &&
        isMarried(user),
      BlockReason.MARRIED,
    ],
    [
      !hasFeature(CertificateFeature.UNDER_18_ALLOWED) && !user.isAdult,
      BlockReason.UNDER_18,
    ],
    [
      !hasFeature(CertificateFeature.EIDAS_ALLOWED) &&
        isEidasIdentifiedUser(user),
      BlockReason.EIDAS_AUTHENTICATION,
    ],
    [
      !hasFeature(CertificateFeature.FOREIGN_MUNICIPALITY_ALLOWED) &&
        isForeignMunicipality(user.kotikuntaKoodi),
      BlockReason.FOREIGN_MUNICIPALITY,
    ],
  ]

  const certificateBlockReasons = certificateFeatureBlockedChecks
    .filter(([isBlocked]) => isBlocked)
    .map(([, reason]) => reason)

  return globalBlockReasons(user).concat(certificateBlockReasons)[0]
}

export const BlockingAnnouncement: React.FC<{
  user?: TodistuksetUser
  certificateType: CertificateTypes
  orderFlowName?: OrderFlowName
}> = ({ user, certificateType, orderFlowName }) => {
  const blockReason = userBlockedReason(user, certificateType, orderFlowName)
  if (blockReason) {
    return <BlockedAnnouncement blockReason={blockReason} />
  }

  return null
}
